<template>
  <div class="complete-button">
    <complete-button
      :is-complete="completeStatus"
      :incomplete-text="customIncompleteButtonText"
      :complete-text="customCompleteButtonText ? customCompleteButtonText : completeText"
      :disabled="disabled"
      :is-secondary="isSecondary"
      @click-action="toggleComplete"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapActions } from 'pinia'
import { useCalendarStore } from '@/stores/calendar'
import { useCompletesStore } from '@/stores/completes'
import { fbApi } from '@/utils/http-api'
import CompleteButton from '@/components/buttons/completes/complete-button.vue'

/**
 * Complete button for schedulable items that are already on the user schedule.
 *
 * RULES
 * - DO NOT use directly in blade, needs a wrapper component for isComplete prop updates
 * - can be for schedulable or plans / programs / challanges
 *
 * COMPLETE FLOW
 * - mark complete
 * - show slider with trackers
 *
 * UNCOMPLETE FLOW
 * - mark incomplete
 * - no sliders
 */
export default defineComponent({
  name: 'ScheduledCompleteButton',
  components: { CompleteButton },
  props: {
    scheduledId: {
      type: Number,
      required: true,
    },
    isComplete: {
      type: Boolean,
      required: true,
    },
    // Used for completes store
    entityId: {
      type: Number,
      required: true,
    },
    entityType: {
      default: 'workout',
      type: String,
      validator: (val) =>
        ['plan', 'workout', 'custom_workout', 'article', 'wellness_video'].includes(val),
    },

    // only used for plan / article complete
    // will be empty string for video / custom workouts
    entityCategory: {
      default: '',
      validator: (val) => ['', 'program', 'challenge', 'routine', 'recipe'].includes(val),
    },

    // only really used for from multi-complete-button
    // which needs to customize only the button, not the slider text
    customIncompleteButtonText: {
      type: String,
      default: 'Mark Complete',
    },

    // only really used for from multi-complete-button
    // which needs to customize only the button, not the slider text
    customCompleteButtonText: {
      type: String,
      default: '',
    },

    // workouts { minutes, difficulty }
    // other trackers in the future
    trackerDefaults: {
      type: Object,
      default: () => ({}),
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    // only pass in if on multi-complete-button and in flyout list
    isSecondary: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['complete-updated'],
  data() {
    return {
      loading: false,
      error: '',
    }
  },
  computed: {
    // mapping entityType to utilize in API calls
    apiType() {
      return this.entityType === 'plan' ? 'programs' : 'schedule'
    },

    // used for workout complete button on complete
    // passed down to modal component
    completeType() {
      // articles
      if (['article'].includes(this.entityType)) {
        // recipes
        if (['recipe'].includes(this.entityCategory)) {
          return 'Recipe'
        }

        return 'Article'
      }

      if (this.entityType === 'wellness_video') {
        return 'Video'
      }

      // program / challenge / routine
      if (this.entityType === 'plan') {
        if (this.entityCategory === 'routine') {
          return 'Routine'
        }

        if (this.entityCategory === 'challenge') {
          return 'Challenge'
        }

        return 'Program'
      }

      // all others
      return 'Workout'
    },

    completeText() {
      return `${this.completeType} Complete`
    },

    // if processing, instantly flip status in UI
    // while XHR is processing
    completeStatus() {
      let isCompleteNow = this.isComplete

      if (this.loading) {
        return !isCompleteNow
      }

      return isCompleteNow
    },
  },
  methods: {
    ...mapActions(useCalendarStore, [
      'setIsComplete',
      'updateSlider',
      'updateDayComplete',
      'setCompleteActionsDisabled',
    ]),
    ...mapActions(useCompletesStore, ['addComplete', 'removeComplete']),

    /**
     * This works in two different ways:
     * plans (programs)
     *  - just toggle is_complete
     *
     * FB videos and custom workouts
     *  - toggle is_complete
     *  - then show a fly over to allow user to customize difficulty and minutes
     *  - fly over is multi-stepped, but saving will update schedule difficulty / minutes
     */
    async toggleComplete() {
      // disable double submit or disabled submits
      if (this.loading || this.disabled) {
        return
      }

      // set loading and instantly toggle state (assuming API will be success)
      // for quick UI.  This will undo if error
      this.loading = true
      this.error = ''
      let plan = null
      let grouping = null
      let stats = null
      let rewardEarned = false

      // only update if completing, to disable other complete buttons
      // while slider shows
      if (this.completeStatus) {
        this.setCompleteActionsDisabled(this.loading)
      }

      try {
        // we have already toggled complete, so api url is backwards
        const res = await fbApi.put(
          `/calendar/${this.apiType}/${this.scheduledId}/${
            this.completeStatus ? 'complete' : 'incomplete'
          }`,
        )

        // powers program complete flyout
        if (res?.body?.data?.programComplete) {
          const { programComplete } = res.body.data
          plan = programComplete.plan
          grouping = programComplete.grouping
          stats = programComplete.stats
          rewardEarned = programComplete.rewardEarned
        }

        // update pinia secheduledItems array
        this.setIsComplete({ id: this.scheduledId, isComplete: this.completeStatus })

        // update pinia completes
        if (this.entityId) {
          if (this.completeStatus) {
            this.addComplete({
              schedulableId: this.entityId,
              schedulableType: this.entityType,
              completes: 1,
            })
          } else {
            this.removeComplete({
              schedulableId: this.entityId,
              schedulableType: this.entityType,
              completes: 1,
            })
          }
        }

        // pass up the food chain
        this.$emit('complete-updated', this.completeStatus)

        // push day complete status to pinia
        if (res?.body?.data?.dayComplete) {
          this.updateDayComplete(res.body.data.dayComplete)
        }

        // schedulable complete slider, only shows if user completeEffects
        // are enabled
        if (this.completeStatus) {
          // default to schedulable
          const sliderPayload = {
            isOpen: true,
            type: 'schedulable',
            completeText: this.completeText,
            scheduledId: this.scheduledId,
            groupingId: null,
            entityId: this.entityId,
            entityType: this.entityType,
            entityCategory: this.entityCategory,
            plan,
            grouping,
            stats,
            trackerDefaults: this.trackerDefaults,
          }

          // program complete overrides
          if (this.entityType === 'plan') {
            sliderPayload.type = 'program'
            sliderPayload.scheduledId = null

            // if direct / normal program complete
            // scheduledId is the groupingId
            sliderPayload.groupingId = this.scheduledId

            // if free program reward earned from program completion
            sliderPayload.rewardEarned = rewardEarned
          }

          this.updateSlider(sliderPayload)
        }
      } catch (e) {
        this.updateSlider({ isOpen: false })
        this.error = 'Error completing'
        console.error(e)
      } finally {
        // re-enable button action and let parent components know
        // complete is over
        this.loading = false
        this.setCompleteActionsDisabled(this.loading)
      }
    },
  },
})
</script>
